import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import { usersSearchQueryOptions } from '../../api/users';
import { FileDropZone } from './FileDropZone';

interface EmployeeLoginPickerProps {
  /** Initial list of logins (e.g. restored from the URL). */
  initialLogins?: string[];
  /** Called with the current list of logins whenever it changes. */
  onChange: (logins: string[]) => void;
}

/**
 * Builds the employee recipient list two ways: a search-as-you-type multi-select
 * (reusing /users/search, adding UPNs as chips) and a CSV of logins (one per
 * line / first column). Logins are matched against Lucca by email server-side.
 */
export function EmployeeLoginPicker({
  initialLogins,
  onChange,
}: EmployeeLoginPickerProps) {
  const { t } = useTranslation();
  const [logins, setLogins] = useState<string[]>(initialLogins ?? []);
  const [q, setQ] = useState('');
  const [debouncedQ] = useDebounce(q.trim(), 250);

  const { data: results = [] } = useQuery(usersSearchQueryOptions(debouncedQ));

  // Set the list and notify the parent together — no effect needed to sync.
  const update = (next: string[]) => {
    setLogins(next);
    onChange(next);
  };

  const add = (login: string) => {
    if (!logins.includes(login)) {
      update([...logins, login]);
    }
    setQ('');
  };
  const remove = (login: string) => update(logins.filter((l) => l !== login));

  const onCsv = async (file: File) => {
    const text = await file.text();
    const parsed = text
      .split(/\r?\n/)
      .map((line) => line.split(/[,;]/)[0]?.trim() ?? '')
      .filter(Boolean)
      // Drop an obvious header row.
      .filter((v) => !/^(login|upn|e-?mail)$/i.test(v));
    update([...new Set([...logins, ...parsed])]);
  };

  const picked = new Set(logins);
  const suggestions = results.filter((u) => !picked.has(u.userPrincipalName));

  return (
    <div className="space-y-3">
      {/* Search-as-you-type */}
      <div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          // This box sits inside the generator's form: Enter here means
          // "I am still picking recipients", never "start the batch".
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          placeholder={t('cardGenerator.employee.searchPlaceholder')}
          className="w-full px-3 py-2 text-sm"
        />
        {/* `q`, not only the debounced value: an added login empties the input,
            and the suggestions must go with it, not 250ms later. */}
        {q.trim().length >= 2 &&
          debouncedQ.length >= 2 &&
          suggestions.length > 0 && (
            <div className="mt-1 max-h-44 overflow-auto border border-rule">
              {suggestions.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => add(u.userPrincipalName)}
                  className="w-full px-2 py-1.5 text-left text-xs text-ink"
                >
                  <span>{u.displayName || u.userPrincipalName}</span>
                  <span className="mono ml-2 text-muted">
                    {u.userPrincipalName}
                  </span>
                </button>
              ))}
            </div>
          )}
      </div>

      {/* CSV of logins — same dropzone as the other uploads on the page. */}
      <FileDropZone
        accept=".csv,text/csv,text/plain"
        hint={t('cardGenerator.employee.csvHint')}
        onFile={(f) => void onCsv(f)}
      />

      {/* Selected logins */}
      {logins.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-blue">
            {t('cardGenerator.employee.selected', { count: logins.length })}
          </p>
          <div className="flex flex-wrap gap-1">
            {logins.map((login) => (
              <span
                key={login}
                className="mono inline-flex items-center gap-1 border border-blue/25 bg-blue/6 px-1.5 py-0.5 text-xs text-blue"
              >
                {login}
                <button
                  type="button"
                  onClick={() => remove(login)}
                  aria-label={t('common.clear')}
                  className="text-blue"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
